import { useCallback } from "react";
import {
  useBrand,
  useCanSpendCredits,
  useIsCompanyOwner,
} from "@/app/(components)/specifics/wrappers/BrandProvider/BrandProvider";
import { ModalStore } from "@/app/stores/ModalStore";
import { UserStore } from "@/app/stores/UserStore";
import { CreditBucket } from "@/app/types/billing/CreditWallet";
import { PageParams } from "@/app/types/common/DefaultProps";
import { UWallet } from "@/app/utils/UWallet";

// Central guard for credit-consuming actions: returns a function that checks whether the
// action is allowed and, when it is not, opens the Billing modal and returns false.
// In a company, a non-owner who cannot spend (viewer) or ran out of shared credits is
// blocked from every credit action whatever the cost, so the informative popup opens
// straight away instead of the cost confirmation.
export function useCreditGuard(params: PageParams) {
  const wallet = UserStore((state) => state.user);
  const isTenant = useBrand() !== null;
  const isCompanyOwner = useIsCompanyOwner();
  const canSpendCredits = useCanSpendCredits();

  return useCallback(
    (cost: number = 1, bucket?: CreditBucket): boolean => {
      if (cost <= 0) {
        return true;
      }

      const available = wallet ? UWallet.spendable(wallet, bucket) : 0;
      const blockedInCompany = isTenant && !isCompanyOwner && (!canSpendCredits || available <= 0);

      if (blockedInCompany || available < cost) {
        ModalStore.getState().openBilling(params);
        return false;
      }
      return true;
    },
    [wallet, params, isTenant, isCompanyOwner, canSpendCredits],
  );
}
